import { useApplicantAPI } from "@certn/api";
import type { components } from "@certn/api/generated-schemas/applicant-portal";
import { sortDescriptorToOrderingParam } from "@certn/cascadia-react/components/data-tables/utils";
import type { SortDescriptor } from "react-aria-components";

import type { ActivityLogFilterState } from "@certn/monitoring";

export type ApplicantMonitoringClient =
  components["schemas"]["ApplicantMonitoringClient"];
export type ApplicantMonitoringClientDetail =
  components["schemas"]["ApplicantMonitoringClientDetail"];
export type ApplicantMonitoringActivityLogRun =
  components["schemas"]["ApplicantMonitoringActivityLogRun"];
export type ApplicantMonitoringActivityLogResponse =
  components["schemas"]["PaginatedApplicantMonitoringActivityLogRunList"];

const CLIENTS_PATH = "/api/internal/applicant/monitoring/clients/" as const;

function clientDetailPath(customerAccountId: string | undefined) {
  return customerAccountId
    ? (`/api/internal/applicant/monitoring/clients/${customerAccountId}/` as const)
    : null;
}

function clientActivityLogPath(customerAccountId: string | undefined) {
  return customerAccountId
    ? (`/api/internal/applicant/monitoring/clients/${customerAccountId}/activity-log/` as const)
    : null;
}

export function useApplicantMonitoringClients() {
  return useApplicantAPI(CLIENTS_PATH);
}

export function useApplicantMonitoringClientDetail(
  customerAccountId: string | undefined
) {
  return useApplicantAPI(clientDetailPath(customerAccountId));
}

export function useApplicantMonitoringActivityLog(
  customerAccountId: string | undefined,
  query: Record<string, string | string[]>
) {
  return useApplicantAPI(clientActivityLogPath(customerAccountId), query);
}

export function buildApplicantActivityLogQuery(
  page: number,
  pageSize: number,
  filters: ActivityLogFilterState,
  sortDescriptor: SortDescriptor
): Record<string, string | string[]> {
  const ordering = sortDescriptorToOrderingParam(sortDescriptor);
  return {
    page: String(page),
    page_size: String(pageSize),
    ...(filters.search.trim() && { search: filters.search.trim() }),
    ...(filters.checkTypes.length && { check_type: filters.checkTypes }),
    ...(filters.monitorStatuses.length && {
      monitor_status: filters.monitorStatuses,
    }),
    ...(filters.resultStatuses.length && {
      result_status: filters.resultStatuses,
    }),
    ...(filters.dateRange && { date_range: filters.dateRange }),
    ...(ordering && { ordering }),
  };
}
